import { Customer } from "@/types/general";
import { AutocompleteInputChangeReason } from "@mui/base/AutocompleteUnstyled/useAutocomplete";
import { Stack } from "@mui/material";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { UserRoundSearch } from "lucide-react";
import { useEffect, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SaleOrdersFormTyping } from "../../types";
import { useShouldSkipCopyAutofill } from "../../CopyHydrationContext";

interface TheirReferenceFieldProps {
    customer?: SaleOrdersFormTyping["customer"];
    customerData?: Customer.Extended;
    isLoading?: boolean;
    mode: SaleOrdersFormTyping["mode"];
    onCreate?: () => void;
}

/**
 * Handling the Sale Order "Their reference" UX as a single visible field with two backing states:
 * 1. Selecting an existing customer contact to keep a live database reference.
 * 2. Typing a one-off free-text value for order-specific cases.
 *
 * Showing only one input to avoid exposing backend implementation details in the UI, while internally
 * keeping contact selection and free-text input as two separate RHF-controlled values.
 *
 * Preserving edit hydration by keeping the initial sale-order value untouched on first render, because
 * the customer field is already present during edit loading and should not be treated as a fresh selection.
 *
 * Clearing stale contact/text state when the customer changes, because both branches are customer-scoped
 * and must not silently carry over into another customer's order.
 *
 * Reapplying only the customer's explicit default reference after the fresh customer payload arrives,
 * because the default comes from async customer data and should be restored only after confirming that
 * the loaded payload still belongs to the currently selected customer.
 *
 * Switching between contact mode and free-text mode based on explicit user actions:
 * selecting a contact activates the linked-reference branch,
 * typing activates the free-text branch,
 * clearing removes both.
 *
 * Coordinating this flow through:
 * refs for customer-transition tracking,
 * RHF controllers for the two backing values,
 * and MUI autocomplete callbacks for distinguishing typing, clearing, and post-selection resets.
 */
export default function TheirReferenceField({
    customer,
    customerData,
    isLoading = false,
    mode,
    onCreate,
}: TheirReferenceFieldProps) {
    const { t } = useTranslation("", {
        keyPrefix: "sales.orders.general.dropdowns.theirReference",
    });
    const { control, setValue } = useFormContext<SaleOrdersFormTyping>();
    const shouldSkipCopyAutofill = useShouldSkipCopyAutofill(customer?.id);

    // Tracking the previous customer to distinguish initial edit hydration from a real customer change.
    const previousCustomerIdRef = useRef<number>();
    // Delaying default-reference prefill until the matching customer payload has been fetched.
    const pendingDefaultCustomerIdRef = useRef<number | null>(null);

    // Controlling the linked-contact branch of the hybrid field through RHF.
    const { field: theirReferenceField, fieldState: theirReferenceFieldState } = useController({
        name: "their_reference",
        control,
    });
    // Controlling the free-text branch of the hybrid field through RHF.
    const { field: theirReferenceNameField, fieldState: theirReferenceNameFieldState } =
        useController({
            name: "their_reference_name",
            control,
        });

    // Clearing both branches together to keep the hybrid field in a single-state mode.
    const clearTheirReference = () => {
        setValue("their_reference", null);
        setValue("their_reference_name", null);
    };

    // Clearing both branches through field handlers when the user explicitly removes the value.
    const clearTheirReferenceByUser = () => {
        theirReferenceField.onChange(null);
        theirReferenceNameField.onChange(null);
    };

    // Reacting to customer-id changes to decide whether the field should preserve edit hydration or reset stale state.
    useEffect(() => {
        const currentCustomerId = customer?.id;
        const previousCustomerId = previousCustomerIdRef.current;

        if (currentCustomerId === previousCustomerId) {
            return;
        }

        previousCustomerIdRef.current = currentCustomerId;

        if (!currentCustomerId) {
            pendingDefaultCustomerIdRef.current = null;
            clearTheirReference();
            return;
        }

        // Skipping the reset during initial edit hydration to preserve the value loaded from the sale order.
        if (
            (mode === "editing" && previousCustomerId === undefined) ||
            shouldSkipCopyAutofill
        ) {
            pendingDefaultCustomerIdRef.current = null;
            return;
        }

        // Clearing stale state on real customer changes before allowing a new default to be applied.
        clearTheirReference();
        pendingDefaultCustomerIdRef.current = currentCustomerId;
    }, [customer?.id, mode, setValue, shouldSkipCopyAutofill]);

    // Reacting to async customer-data arrival to apply the customer's default reference only after the correct payload is loaded.
    useEffect(() => {
        const currentCustomerId = customer?.id;

        if (!currentCustomerId || pendingDefaultCustomerIdRef.current !== currentCustomerId) {
            return;
        }

        // Prefilling only the explicit customer default reference after the fresh customer payload is ready.
        if (customerData?.reference) {
            setValue("their_reference", {
                id: customerData.reference.id,
                name: customerData.reference.name,
            });
        }

        pendingDefaultCustomerIdRef.current = null;
    }, [customer?.id, customerData, setValue]);

    const handleInputChange = (value: string, reason: AutocompleteInputChangeReason): void => {
        // Ignoring MUI reset events because they are emitted after option selection.
        if (reason === "reset") {
            return;
        }

        if (reason === "clear") {
            clearTheirReferenceByUser();
            return;
        }

        // Switching the field into free-text mode as soon as the user starts typing.
        theirReferenceField.onChange(null);
        theirReferenceNameField.onChange(value);
    };

    const handleChange = (selectedContact: Customer.Components.Contact | undefined): void => {
        if (!selectedContact) {
            clearTheirReferenceByUser();
            return;
        }

        // Switching the field into linked-contact mode when the user explicitly picks a contact.
        theirReferenceField.onChange({
            id: selectedContact.id,
            name: selectedContact.name,
        });
        theirReferenceNameField.onChange(null);
    };

    return (
        <BaseDropdown
            value={theirReferenceField.value ?? null}
            // Keeping the single visible input synchronized with either the selected contact or the typed fallback text.
            inputValue={theirReferenceField.value?.name ?? theirReferenceNameField.value ?? ""}
            error={theirReferenceNameFieldState.error ?? theirReferenceFieldState.error}
            label={t("label")}
            placeholder={t("placeholder")}
            options={customerData?.contacts}
            getter={{
                label: (item) => item.name,
                key: (item) => item.id,
            }}
            disabled={!customer}
            disableReason={
                <Stack direction={"row"} spacing={2}>
                    <UserRoundSearch size={20} />
                    <span>{t("blockedBeforeCustomers")}</span>
                </Stack>
            }
            action={
                !customerData?.is_person
                    ? {
                          title: t("buttons.create"),
                          onClick: onCreate,
                      }
                    : undefined
            }
            autocomplete
            freeSolo
            isLoading={isLoading}
            onInputChange={(_, value, reason) => handleInputChange(value, reason)}
            onChange={handleChange}
        />
    );
}
